import React from 'react';
import { Label, OverviewHeader } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { RoleService } from 'Services';
import { FieldArray, useFormikContext } from 'formik';
import { RULE_INPUT_PARAMETER_NAMES } from 'Common/enums';
import { FormikFieldTextArea } from 'Components/FormikComponents';
import Conclusion from '../../AddRulePage/RuleStatement/Conclusion';
import ConclusionTemplated from './ConclusionTemplated';
import Condition from './Condition';

import styles from './RuleStatement.css';

type RuleStatementProps = {
    currentRule: Rule;
    parameterData: UseGetParametersResponse | undefined;
    disabledInputFields?: DisabledInputFields;
    mutationError: any;
    listData?: ListApiGetResponse;
    conclusionFlagListData?: ConclusionListApiGetResponse[];
};

const RuleStatement = ({
    currentRule,
    parameterData,
    mutationError,
    listData,
    conclusionFlagListData,
    disabledInputFields
}: RuleStatementProps) => {
    const { t } = useTranslation();
    const {
        errors,
        values,
        initialValues,
        isSubmitting,
        setFieldValue,
        handleChange,
        handleBlur
    } = useFormikContext<TemplatedRuleFormValues>();
    const getValueError = (inputParamName: string) => {
        return !!errors[inputParamName] || mutationError?.data?.hasValueError?.param === inputParamName;
    };

    const { isThresholdEditorOnly } = RoleService;
    const readOnly = isThresholdEditorOnly || disabledInputFields?.disableDefinitionChange;

    return (
        <OverviewHeader dataTestId='rule-stmt' leftTitle={t('Rule Statement')}>
            <div className={styles.conclusionMessageLabel}>
                <Label label={t('ruleConditionInfo')} id='condition-info' />
            </div>

            <div className={styles.sectionHeader}>{t('Conditions')}</div>
            <div className={styles.conditionWrapper}>
                <div className={styles.conditionHeaders}>
                    <div>Parameter</div>
                    <div>Operator</div>
                    <div>Value</div>
                </div>

                {currentRule.inputs?.map((input, index) => {
                    return (
                        <div key={`${input.parameter.name + index}`} className={styles.conditionRow}>
                            <Condition
                                key={`${input.parameter.name + index}`}
                                input={input}
                                index={index}
                                isSubmitting={isSubmitting}
                                formikValues={values}
                                parameterData={parameterData}
                                listData={listData}
                                initialValues={initialValues}
                                error={
                                    input.parameter.type === 'list'
                                        ? errors.lists ?? {}
                                        : getValueError(input.parameter.name)
                                }
                                errorMsg={String(
                                    input.parameter.type === 'list' ? errors.lists ?? {} : errors[input.parameter.name]
                                )}
                                setFieldValue={setFieldValue}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                            />
                        </div>
                    );
                })}
            </div>

            <div className={styles.sectionHeader}>{t('Conclusion')}</div>

            <div className={styles.conditionWrapper}>
                <div className={styles.messageLabel}>
                    <Label className={styles.messageLabel} label={t('ruleConclusionRejectInfo')} id='info' />
                </div>
                {/**
                 * @note Simple Rule has Templated and Non-Templated Rules.
                 * Or use Templated for old Rules without actions.
                 */}
                {!currentRule.nonTemplated || !currentRule.actions ? (
                    <ConclusionTemplated
                        outputDescription={currentRule.outputDescription}
                        score={currentRule.parameters.score}
                        isSubmitting={isSubmitting}
                        error={getValueError(RULE_INPUT_PARAMETER_NAMES.SCORE)}
                        errorMsg={String(errors[RULE_INPUT_PARAMETER_NAMES.SCORE])}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                    />
                ) : (
                    <FieldArray
                        name='actions'
                        render={() => (
                            <>
                                {values.actions &&
                                    values.actions.length > 0 &&
                                    values.actions.map((_action: Action, index: number) => {
                                        return (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <div key={index} className={styles.conditionRow}>
                                                <Conclusion
                                                    index={index}
                                                    conclusionFlagListData={conclusionFlagListData}
                                                    parameterData={parameterData}
                                                    listData={listData?.result}
                                                    readOnly={readOnly ?? false}
                                                />
                                            </div>
                                        );
                                    })}
                            </>
                        )}
                    />
                )}
            </div>

            <div className={styles.sectionHeader}>{t('Comment (Optional)')}</div>
            <FormikFieldTextArea
                dataTestId='comment-field'
                id={RULE_INPUT_PARAMETER_NAMES.COMMENT}
                name={RULE_INPUT_PARAMETER_NAMES.COMMENT}
                lineNum={1}
                className={styles.commentField}
                onChange={handleChange}
                onBlur={handleBlur}
                error={getValueError(RULE_INPUT_PARAMETER_NAMES.COMMENT)}
                errorMsg={String(errors[RULE_INPUT_PARAMETER_NAMES.COMMENT])}
                disabled={isSubmitting}
            />
        </OverviewHeader>
    );
};

export default React.memo(RuleStatement);
